import React, { useState, useRef, useEffect } from 'react';
import { useTranslations } from '../translations';
import { ProductSuspensionReason } from '../services/api.types';
import './AdminCard.css';

interface AdminCardProps {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  location: string;
  datePosted: string;
  views: number;
  price: string;
  negotiable: boolean;
  category: string;
  subCategory?: string | null;
  status?: 'ACTIVE' | 'EXPIRED';
  onClick?: (id: string) => void;
  onApprove?: () => void;
  onDecline?: () => void;
  currentTab?: 'ACTIVE' | 'EXPIRED' | 'PENDING' | 'DECLINED';
  complaints?: number;
  suspensionReason?: ProductSuspensionReason | null;
}

const AdminCard: React.FC<AdminCardProps> = ({
  id,
  title,
  description,
  imageUrl,
  location,
  datePosted,
  views,
  price,
  negotiable,
  category,
  subCategory,
  status = 'ACTIVE',
  onClick,
  onApprove,
  onDecline,
  currentTab,
  complaints,
  suspensionReason,
}) => {
  const t = useTranslations();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const truncateDescription = (text: string | undefined, maxLength: number = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onApprove?.();
  };

  const handleDecline = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onDecline?.();
  };

  const handleCardClick = () => {
    onClick?.(id);
  };

  return (
    <div
      className="admin-card"
      onClick={handleCardClick}
    >
      <div className="admin-card-image">
        {imageUrl ? (
          <img src={imageUrl} alt={title} />
        ) : (
          <div style={{ width: '100%', height: '100%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
            {t.adminCard.noImage}
          </div>
        )}
      </div>
      
      <div className="admin-card-details">
        {currentTab && (onApprove || onDecline) && (
          <>
            <button className="admin-card-menu-button" onClick={handleMenuToggle}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 6C10.9167 6 11.6667 5.25 11.6667 4.33333C11.6667 3.41667 10.9167 2.66667 10 2.66667C9.08333 2.66667 8.33333 3.41667 8.33333 4.33333C8.33333 5.25 9.08333 6 10 6ZM10 8.33333C9.08333 8.33333 8.33333 9.08333 8.33333 10C8.33333 10.9167 9.08333 11.6667 10 11.6667C10.9167 11.6667 11.6667 10.9167 11.6667 10C11.6667 9.08333 10.9167 8.33333 10 8.33333ZM10 13.3333C9.08333 13.3333 8.33333 14.0833 8.33333 15C8.33333 15.9167 9.08333 16.6667 10 16.6667C10.9167 16.6667 11.6667 15.9167 11.6667 15C11.6667 14.0833 10.9167 13.3333 10 13.3333Z" fill="#596255"/>
              </svg>
            </button>
            {showMenu && (
              <div className="admin-card-dropdown" ref={menuRef}>
                {currentTab === 'PENDING' && (
                  <>
                    {onApprove && (
                      <button className="admin-dropdown-item" onClick={handleApprove}>
                        {t.administration.products.approve}
                      </button>
                    )}
                    {onDecline && (
                      <button className="admin-dropdown-item admin-dropdown-item-delete" onClick={handleDecline}>
                        {t.administration.products.decline}
                      </button>
                    )}
                  </>
                )}
                {currentTab === 'ACTIVE' && onDecline && (
                  <button className="admin-dropdown-item admin-dropdown-item-delete" onClick={handleDecline}>
                    {t.administration.products.decline}
                  </button>
                )}
                {currentTab === 'DECLINED' && onApprove && (
                  <button className="admin-dropdown-item" onClick={handleApprove}>
                    {t.administration.products.approve}
                  </button>
                )}
                {currentTab === 'EXPIRED' && onApprove && (
                  <button className="admin-dropdown-item" onClick={handleApprove}>
                    {t.administration.products.approve}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        <div className="admin-card-info">
          <div className="admin-card-title-row">
            <div className="admin-card-title">{title}</div>
            {suspensionReason && (
              <span className="admin-card-suspension-badge">
                {suspensionReason === 'OWNER_BLOCKED'
                  ? t.administration.products.ownerBlocked
                  : t.administration.products.adminBlocked}
              </span>
            )}
          </div>
          <div className="admin-card-breadcrumb">
            {category}
            {subCategory && (
              <>
                <span className="admin-breadcrumb-separator"> / </span>
                {subCategory}
              </>
            )}
          </div>
          {description && (
            <div className="admin-card-description">{truncateDescription(description)}</div>
          )}

          <div className="admin-card-meta">
            <div className="admin-card-meta-item">
              <div className="admin-icon">
                <svg width="11" height="14" viewBox="0 0 11 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M0 5.28133C0 2.36867 2.39267 0 5.33333 0C8.274 0 10.6667 2.36867 10.6667 5.28133C10.6667 8.93133 5.96733 13.0007 5.76733 13.172C5.642 13.2793 5.488 13.3333 5.33333 13.3333C5.17867 13.3333 5.02467 13.2793 4.89933 13.172C4.69933 13.0007 0 8.93133 0 5.28133ZM5.33333 11.764C4.21667 10.708 1.33333 7.74333 1.33333 5.28133C1.33333 3.10467 3.12733 1.33333 5.33333 1.33333C7.53933 1.33333 9.33333 3.10467 9.33333 5.28133C9.33333 7.74333 6.45 10.708 5.33333 11.764ZM3 4.99967C3 3.713 4.04667 2.66633 5.33333 2.66633C6.62 2.66633 7.66667 3.713 7.66667 4.99967C7.66667 6.28633 6.62 7.333 5.33333 7.333C4.04667 7.333 3 6.28633 3 4.99967ZM4.33333 4.99967C4.33333 5.551 4.782 5.99967 5.33333 5.99967C5.88467 5.99967 6.33333 5.551 6.33333 4.99967C6.33333 4.44833 5.88467 3.99967 5.33333 3.99967C4.782 3.99967 4.33333 4.44833 4.33333 4.99967Z" fill="#596255"/>
                </svg>
              </div>
              <span className="admin-meta-text">{location}</span>
            </div>
            
            <div className="admin-card-meta-item">
              <div className="admin-icon">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M0 6.66667C0 2.99067 2.99067 0 6.66667 0C10.3427 0 13.3333 2.99067 13.3333 6.66667C13.3333 10.3427 10.3427 13.3333 6.66667 13.3333C2.99067 13.3333 0 10.3427 0 6.66667ZM6.66667 12C3.726 12 1.33333 9.60733 1.33333 6.66667C1.33333 3.726 3.726 1.33333 6.66667 1.33333C9.60733 1.33333 12 3.726 12 6.66667C12 9.60733 9.60733 12 6.66667 12ZM9.33333 6H7.33333V4C7.33333 3.63133 7.03467 3.33333 6.66667 3.33333C6.29867 3.33333 6 3.63133 6 4V6.66667C6 7.03533 6.29867 7.33333 6.66667 7.33333H9.33333C9.702 7.33333 10 7.03533 10 6.66667C10 6.298 9.702 6 9.33333 6Z" fill="#596255"/>
                </svg>
              </div>
              <span className="admin-meta-text">{datePosted}</span>
            </div>
            
            <div className="admin-card-meta-item">
              <div className="admin-icon">
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6.4865 0.00306476C10.4705 -0.121602 12.8198 3.5944 13.2452 4.33573C13.3625 4.54106 13.3625 4.79373 13.2452 4.99906C12.6758 5.99173 10.5318 9.23773 6.84717 9.33173C6.7925 9.33307 6.73783 9.33373 6.68317 9.33373C2.7745 9.33373 0.507833 5.7304 0.0885 4.99906C-0.0295 4.79373 -0.0295 4.54106 0.0885 4.33573C0.657833 3.34307 2.80117 0.0963981 6.4865 0.00306476ZM6.81317 7.99906C3.9425 8.06573 2.06983 5.6104 1.4485 4.6644C2.1325 3.5944 3.85517 1.40373 6.5205 1.33573C9.37983 1.26306 11.2632 3.7244 11.8845 4.6704C11.2012 5.7404 9.47783 7.93106 6.81317 7.99906ZM4.33337 4.66733C4.33337 3.38066 5.38003 2.334 6.6667 2.334C7.95337 2.334 9.00003 3.38066 9.00003 4.66733C9.00003 5.954 7.95337 7.00066 6.6667 7.00066C5.38003 7.00066 4.33337 5.954 4.33337 4.66733ZM5.6667 4.66733C5.6667 5.21866 6.11537 5.66733 6.6667 5.66733C7.21803 5.66733 7.6667 5.21866 7.6667 4.66733C7.6667 4.116 7.21803 3.66733 6.6667 3.66733C6.11537 3.66733 5.6667 4.116 5.6667 4.66733Z" fill="#596255"/>
                </svg>
              </div>
              <span className="admin-meta-text">{t.productDetail.views.replace('{count}', views.toString())}</span>
            </div>

            {complaints !== undefined && complaints > 0 && (
              <div className="admin-card-meta-item">
                <div className="admin-icon">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.99967 0.333008C3.31967 0.333008 0.333008 3.31967 0.333008 6.99967C0.333008 10.6797 3.31967 13.6663 6.99967 13.6663C10.6797 13.6663 13.6663 10.6797 13.6663 6.99967C13.6663 3.31967 10.6797 0.333008 6.99967 0.333008ZM7.66634 10.333H6.33301V8.99967H7.66634V10.333ZM7.66634 7.66634H6.33301V3.66634H7.66634V7.66634Z" fill="#B12A00"/>
                  </svg>
                </div>
                <span className="admin-meta-text" style={{ color: '#B12A00' }}>{t.adminCard.complaints.replace('{count}', complaints.toString())}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="admin-card-bottom">
          <div className="admin-card-price">
            <div className="admin-price-label">{t.createProduct.priceLabel}</div>
            <div className="admin-price-value">{price}</div>
            <div className="admin-negotiable">{negotiable ? t.productDetail.negotiable : t.productDetail.nonNegotiable}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCard;
